import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('pointerover', this.#handleThumbnailHover, { signal });
    this.addEventListener('click', this.#handleImageClick, { signal });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });

    if (this.hasAttribute('data-hover-zoom')) {
      this.addEventListener('pointermove', this.#handleZoomPointerMove, { signal });
      this.addEventListener('pointerleave', this.#hideHoverZoom, { signal });
      window.addEventListener('scroll', this.#hideHoverZoom, { signal, passive: true });
      window.addEventListener('resize', this.#hideHoverZoom, { signal, passive: true });
    }
  }

  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #zoomLens = null;
  /** @type {HTMLElement | null} */
  #zoomPanel = null;
  #shareCloseTimeout = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#zoomLens?.remove();
    this.#zoomPanel?.remove();
    window.clearTimeout(this.#shareCloseTimeout);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Selects product media when a desktop mouse hovers over its thumbnail.
   * Clicking remains available for touch devices and keyboard users.
   *
   * @param {PointerEvent} event
   */
  #handleThumbnailHover = (event) => {
    if (event.pointerType !== 'mouse' || !window.matchMedia('(min-width: 750px) and (hover: hover)').matches) return;
    if (!(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    if (!(thumbnail instanceof HTMLButtonElement)) return;
    if (thumbnail.closest('media-gallery') !== this) return;
    if (thumbnail.closest('.media-gallery__mobile-controls')) return;
    if (thumbnail.getAttribute('aria-selected') === 'true') return;

    const slideshow = this.slideshow;
    const thumbnailIndex = slideshow?.refs.thumbnails?.indexOf(thumbnail) ?? -1;
    if (!slideshow || thumbnailIndex < 0) return;

    slideshow.select(thumbnailIndex, undefined, { animate: false, centerThumbnail: false });
  };

  /**
   * Opens the existing full-view gallery when the active desktop image is clicked.
   *
   * @param {MouseEvent} event
   */
  #handleImageClick = (event) => {
    if (!window.matchMedia('(min-width: 750px)').matches || !(event.target instanceof Element)) return;

    const overflowTrigger = event.target.closest('[data-thumbnail-overflow-trigger]');
    if (overflowTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      const overflowIndex = Number(overflowTrigger.dataset.thumbnailOverflowIndex);
      if (!Number.isInteger(overflowIndex)) return;

      this.#closeShareMenu();
      this.#hideHoverZoom();
      this.slideshow?.select(overflowIndex, undefined, { animate: false, centerThumbnail: false });
      this.zoom(overflowIndex, /** @type {PointerEvent} */ (event));
      return;
    }

    const image = event.target.closest('img.product-media__image');
    if (!(image instanceof HTMLImageElement) || image.closest('zoom-dialog')) return;

    const slide = image.closest('slideshow-slide');
    if (slide?.getAttribute('aria-hidden') === 'true') return;

    this.#closeShareMenu();
    this.#hideHoverZoom();
    this.zoomCurrent(/** @type {PointerEvent} */ (event));
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('.product-media-gallery__share')) return;
    this.#closeShareMenu();
  };

  /**
   * @param {MouseEvent} [event]
   */
  closeShareMenu(event) {
    event?.preventDefault();
    this.#closeShareMenu();
  }

  handleShareOptionClick() {
    window.setTimeout(() => this.#closeShareMenu(), 0);
  }

  #closeShareMenu() {
    window.clearTimeout(this.#shareCloseTimeout);
    const share = this.querySelector('.product-media-gallery__share[open]');
    if (!(share instanceof HTMLDetailsElement)) return;

    share.classList.add('is-closing');
    this.#shareCloseTimeout = window.setTimeout(() => {
      share.removeAttribute('open');
      share.classList.remove('is-closing');
    }, 180);
  }

  /**
   * @param {MouseEvent} event
   */
  async copyProductLink(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-share-url]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const url = button.dataset.shareUrl;
    const label = button.querySelector('[data-copy-label]');
    if (!url) return;

    if (label instanceof HTMLElement) label.textContent = 'Copied!';

    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(url);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.setAttribute('readonly', '');
      document.body.append(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }

    if (!(label instanceof HTMLElement)) return;
    window.clearTimeout(this.#shareCloseTimeout);
    this.#shareCloseTimeout = window.setTimeout(() => {
      this.#closeShareMenu();
      window.setTimeout(() => {
        label.textContent = 'Copy link';
      }, 180);
    }, 1000);
  }

  #createHoverZoomElements() {
    if (this.#zoomLens && this.#zoomPanel) return;

    this.#zoomLens = document.createElement('div');
    this.#zoomLens.className = 'product-media-hover-zoom__lens';
    this.#zoomLens.setAttribute('aria-hidden', 'true');

    this.#zoomPanel = document.createElement('div');
    this.#zoomPanel.className = 'product-media-hover-zoom__panel';
    this.#zoomPanel.setAttribute('aria-hidden', 'true');

    document.body.append(this.#zoomLens, this.#zoomPanel);
  }

  /**
   * Returns the portion of an image element occupied by the rendered image,
   * excluding any empty space introduced by object-fit: contain.
   *
   * @param {HTMLImageElement} image
   */
  #getRenderedImageRect(image) {
    const box = image.getBoundingClientRect();
    const naturalRatio = image.naturalWidth / image.naturalHeight;
    const boxRatio = box.width / box.height;
    const objectFit = getComputedStyle(image).objectFit;

    if (!image.naturalWidth || !image.naturalHeight || objectFit !== 'contain') return box;

    if (naturalRatio > boxRatio) {
      const height = box.width / naturalRatio;
      return new DOMRect(box.left, box.top + (box.height - height) / 2, box.width, height);
    }

    const width = box.height * naturalRatio;
    return new DOMRect(box.left + (box.width - width) / 2, box.top, width, box.height);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleZoomPointerMove = (event) => {
    if (
      event.pointerType !== 'mouse' ||
      !window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches ||
      !(event.target instanceof Element)
    ) {
      this.#hideHoverZoom();
      return;
    }

    const image = event.target.closest('img.product-media__image');
    if (!(image instanceof HTMLImageElement) || image.closest('zoom-dialog')) {
      this.#hideHoverZoom();
      return;
    }

    const slide = image.closest('slideshow-slide');
    if (slide?.getAttribute('aria-hidden') === 'true') {
      this.#hideHoverZoom();
      return;
    }

    const imageRect = this.#getRenderedImageRect(image);
    if (
      event.clientX < imageRect.left ||
      event.clientX > imageRect.right ||
      event.clientY < imageRect.top ||
      event.clientY > imageRect.bottom
    ) {
      this.#hideHoverZoom();
      return;
    }

    const productGrid = this.closest('.product-information__grid');
    const details = productGrid?.querySelector('.product-details');
    if (!(details instanceof HTMLElement)) return;

    const detailsRect = details.getBoundingClientRect();
    const panelPadding = 8;
    const panelLeftGutter = 20;
    const panelRightGutter = Math.max(24, Math.min(48, window.innerWidth * 0.02));
    const panelLeft = Math.max(panelPadding, detailsRect.left + panelLeftGutter);
    const actualPanelWidth = window.innerWidth - panelLeft - panelRightGutter;
    const panelHeight = Math.min(imageRect.height, window.innerHeight - panelPadding * 2);

    if (actualPanelWidth < 300 || panelHeight < 300) {
      this.#hideHoverZoom();
      return;
    }

    this.#createHoverZoomElements();
    if (!this.#zoomLens || !this.#zoomPanel) return;

    const zoom = 2.5;
    const lensWidth = Math.min(imageRect.width, actualPanelWidth / zoom);
    const lensHeight = Math.min(imageRect.height, panelHeight / zoom);
    const lensLeft = Math.min(
      imageRect.right - lensWidth,
      Math.max(imageRect.left, event.clientX - lensWidth / 2)
    );
    const lensTop = Math.min(
      imageRect.bottom - lensHeight,
      Math.max(imageRect.top, event.clientY - lensHeight / 2)
    );
    const scale = Math.min(actualPanelWidth / lensWidth, panelHeight / lensHeight);
    const highResolutionUrl = image.getAttribute('data_max_resolution') || image.currentSrc || image.src;
    const panelTop = Math.min(
      Math.max(panelPadding, imageRect.top),
      window.innerHeight - panelHeight - panelPadding
    );

    Object.assign(this.#zoomLens.style, {
      display: 'block',
      left: `${lensLeft}px`,
      top: `${lensTop}px`,
      width: `${lensWidth}px`,
      height: `${lensHeight}px`,
    });

    Object.assign(this.#zoomPanel.style, {
      display: 'block',
      left: `${panelLeft}px`,
      top: `${panelTop}px`,
      width: `${actualPanelWidth}px`,
      height: `${panelHeight}px`,
      backgroundImage: `url("${highResolutionUrl.replace(/"/g, '%22')}")`,
      backgroundSize: `${imageRect.width * scale}px ${imageRect.height * scale}px`,
      backgroundPosition: `${-(lensLeft - imageRect.left) * scale}px ${-(lensTop - imageRect.top) * scale}px`,
    });
  };

  #hideHoverZoom = () => {
    if (this.#zoomLens) this.#zoomLens.style.display = 'none';
    if (this.#zoomPanel) this.#zoomPanel.style.display = 'none';
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the currently selected slideshow media.
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const index = this.slideshow?.current ?? 0;
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
